/**
 * AI Monitoring Service for tracking AI service failures and performance
 * Provides comprehensive logging, metrics, and alerting for AI operations
 */

export interface AIFailureData {
  context: string;
  errorType: string;
  error: string;
  attempt?: number;
  maxAttempts?: number;
  timestamp: string;
  userAgent?: string;
  stack?: string;
  statusCode?: number;
  userId?: string;
  sessionId?: string;
}

export interface AIMetrics {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  fallbackUsage: number;
  averageResponseTime: number;
  errorsByType: Record<string, number>;
  rateLimitHits: number;
}

export class AIMonitoringService {
  private static metrics: AIMetrics = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    fallbackUsage: 0,
    averageResponseTime: 0,
    errorsByType: {},
    rateLimitHits: 0
  };

  private static responseTimes: number[] = [];
  private static readonly MAX_RESPONSE_TIME_SAMPLES = 100;

  /**
   * Log AI service failure with comprehensive data collection
   */
  static logFailure(failureData: AIFailureData): void {
    // Update metrics
    this.updateFailureMetrics(failureData);

    // Enhanced logging for development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 AI Service Failure - ${failureData.context}`);
      console.error('Error Type:', failureData.errorType);
      console.error('Message:', failureData.error);
      if (failureData.attempt && failureData.maxAttempts) {
        console.error(`Attempt: ${failureData.attempt}/${failureData.maxAttempts}`);
      }
      console.error('Timestamp:', failureData.timestamp);
      if (failureData.statusCode) {
        console.error('Status Code:', failureData.statusCode);
      }
      if (failureData.stack) {
        console.error('Stack:', failureData.stack);
      }
      console.groupEnd();
    }

    // Send to external monitoring services
    this.sendToExternalServices(failureData);
  }

  /**
   * Log successful AI operation
   */
  static logSuccess(context: string, responseTime: number): void {
    this.metrics.totalRequests++;
    this.metrics.successfulRequests++;
    
    // Track response times
    this.responseTimes.push(responseTime);
    if (this.responseTimes.length > this.MAX_RESPONSE_TIME_SAMPLES) {
      this.responseTimes.shift();
    }
    
    // Update average response time
    this.metrics.averageResponseTime = 
      this.responseTimes.reduce((sum, time) => sum + time, 0) / this.responseTimes.length;

    // Log slow responses
    if (responseTime > 5000) { // 5 seconds
      console.warn(`⚠️ Slow AI response for ${context}: ${responseTime}ms`);
    }
  }

  /**
   * Log fallback usage
   */
  static logFallbackUsage(context: string, reason: string): void {
    this.metrics.fallbackUsage++;
    
    console.info(`🔄 Fallback used for ${context}: ${reason}`);
    
    // Send fallback usage to monitoring
    this.sendToExternalServices({
      context,
      errorType: 'FALLBACK_USED',
      error: reason,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server'
    });
  }

  /**
   * Log rate limit hit
   */
  static logRateLimit(context: string): void {
    this.metrics.rateLimitHits++;
    
    console.warn(`⏱️ Rate limit hit for ${context}`);
    
    this.sendToExternalServices({
      context,
      errorType: 'RATE_LIMITED',
      error: 'Rate limit exceeded',
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server'
    });
  }

  /**
   * Get current AI service metrics
   */
  static getMetrics(): AIMetrics {
    return { ...this.metrics };
  }

  /**
   * Reset metrics (useful for testing)
   */
  static resetMetrics(): void {
    this.metrics = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      fallbackUsage: 0,
      averageResponseTime: 0,
      errorsByType: {},
      rateLimitHits: 0
    };
    this.responseTimes = [];
  }

  /**
   * Get health status of AI services
   */
  static getHealthStatus(): {
    status: 'healthy' | 'degraded' | 'unhealthy';
    successRate: number;
    averageResponseTime: number;
    issues: string[];
  } {
    const successRate = this.metrics.totalRequests > 0 
      ? (this.metrics.successfulRequests / this.metrics.totalRequests) * 100 
      : 100;

    const issues: string[] = [];
    let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';

    // Check success rate
    if (successRate < 50) {
      status = 'unhealthy';
      issues.push('Success rate below 50%');
    } else if (successRate < 80) {
      status = 'degraded';
      issues.push('Success rate below 80%');
    }

    // Check response time
    if (this.metrics.averageResponseTime > 10000) {
      status = status === 'healthy' ? 'degraded' : 'unhealthy';
      issues.push('Average response time above 10 seconds');
    } else if (this.metrics.averageResponseTime > 5000) {
      if (status === 'healthy') status = 'degraded';
      issues.push('Average response time above 5 seconds');
    }

    // Check rate limiting
    if (this.metrics.rateLimitHits > 10) {
      if (status === 'healthy') status = 'degraded';
      issues.push('High rate limit hits');
    }

    // Check fallback usage
    const fallbackRate = this.metrics.totalRequests > 0 
      ? (this.metrics.fallbackUsage / this.metrics.totalRequests) * 100 
      : 0;
    
    if (fallbackRate > 50) {
      status = 'unhealthy';
      issues.push('Fallback usage above 50%');
    } else if (fallbackRate > 20) {
      if (status === 'healthy') status = 'degraded';
      issues.push('Fallback usage above 20%');
    }

    return {
      status,
      successRate,
      averageResponseTime: this.metrics.averageResponseTime,
      issues
    };
  }

  /**
   * Update failure metrics
   */
  private static updateFailureMetrics(failureData: AIFailureData): void {
    this.metrics.totalRequests++;
    this.metrics.failedRequests++;
    
    // Track errors by type
    if (!this.metrics.errorsByType[failureData.errorType]) {
      this.metrics.errorsByType[failureData.errorType] = 0;
    }
    this.metrics.errorsByType[failureData.errorType]++;

    // Track rate limit hits
    if (failureData.errorType === 'RATE_LIMITED') {
      this.metrics.rateLimitHits++;
    }

    // Track fallback usage
    if (failureData.errorType === 'FALLBACK_USED') {
      this.metrics.fallbackUsage++;
    }
  }

  /**
   * Send failure data to external monitoring services
   */
  private static sendToExternalServices(failureData: AIFailureData): void {
    // Only send in production or when monitoring is explicitly enabled
    if (process.env.NODE_ENV !== 'production' && !process.env.ENABLE_AI_MONITORING) {
      return;
    }

    // Send to multiple monitoring services for redundancy
    const monitoringPromises = [
      this.sendToCustomEndpoint(failureData),
      this.sendToConsoleLog(failureData), // Always log to console as backup
    ];

    // Add external services if configured
    if (process.env.SENTRY_DSN) {
      monitoringPromises.push(this.sendToSentry(failureData));
    }

    if (process.env.DATADOG_API_KEY) {
      monitoringPromises.push(this.sendToDatadog(failureData));
    }

    // Execute all monitoring calls without blocking
    Promise.allSettled(monitoringPromises).catch(error => {
      console.warn('Some monitoring services failed:', error);
    });
  }

  /**
   * Send to custom monitoring endpoint
   */
  private static async sendToCustomEndpoint(failureData: AIFailureData): Promise<void> {
    try {
      const response = await fetch('/api/monitoring/ai-failures', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(failureData)
      });

      if (!response.ok) {
        throw new Error(`Monitoring endpoint failed: ${response.status}`);
      }
    } catch (error) {
      console.warn('Failed to send to custom monitoring endpoint:', error);
    }
  }

  /**
   * Send to console log (always available fallback)
   */
  private static async sendToConsoleLog(failureData: AIFailureData): Promise<void> {
    console.error('AI Service Monitoring:', failureData);
  }

  /**
   * Send to Sentry (if configured)
   */
  private static async sendToSentry(failureData: AIFailureData): Promise<void> {
    try {
      // This would integrate with Sentry SDK
      // For now, just log that it would be sent
      console.info('Would send to Sentry:', failureData.context, failureData.errorType);
    } catch (error) {
      console.warn('Failed to send to Sentry:', error);
    }
  }

  /**
   * Send to Datadog (if configured)
   */
  private static async sendToDatadog(failureData: AIFailureData): Promise<void> {
    try {
      // This would integrate with Datadog API
      // For now, just log that it would be sent
      console.info('Would send to Datadog:', failureData.context, failureData.errorType);
    } catch (error) {
      console.warn('Failed to send to Datadog:', error);
    }
  }

  /**
   * Create alert if error rate is too high
   */
  static checkAndCreateAlerts(): void {
    const health = this.getHealthStatus();
    
    if (health.status === 'unhealthy') {
      console.error('🚨 AI Service Alert: System is unhealthy', {
        successRate: health.successRate,
        issues: health.issues,
        metrics: this.metrics
      });
      
      // In production, this would send alerts via email, Slack, etc.
      this.sendAlert('AI Service Unhealthy', health.issues.join(', '));
    } else if (health.status === 'degraded') {
      console.warn('⚠️ AI Service Warning: System is degraded', {
        successRate: health.successRate,
        issues: health.issues
      });
    }
  }

  /**
   * Send alert to configured channels
   */
  private static sendAlert(title: string, message: string): void {
    // In production, integrate with alerting systems
    console.error(`ALERT: ${title} - ${message}`);
    
    // Could integrate with:
    // - Email notifications
    // - Slack webhooks
    // - PagerDuty
    // - Discord webhooks
    // - SMS alerts
  }
}